<template>
  <material-card
    style="margin-top:30px"
    scrollable
    :title="$t('components.plancreationwizard.reports_data_grouping')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="bubble_chart"
  >
    <v-form ref="formReportGroup" v-model="validForm" :disabled="disabled">
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <span>
              {{ $t("components.plancreationwizard.data_grouping_helptext") }}
            </span>
          </v-col>
          <v-col cols="6">
            <auto-select
              :value="defaultDataGrouping"
              :label="$t('components.plancreationwizard.default_data_grouping')"
              :rules="[$rules.required]"
              class="required"
              :items="defaultDataGroupingItems"
              @change="$emit('update-default', $event)"
            ></auto-select>
          </v-col>
          <v-col cols="6">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-container pa-0>
                  <v-row no-gutters>
                    <v-col>
                      <auto-select
                        :clearable="true"
                        :value="additionalDataGrouping"
                        :label="
                          $t(
                            'components.plancreationwizard.additional_data_grouping'
                          )
                        "
                        :items="additionalDataGroupingItems"
                        @change="$emit('update-additional', $event)"
                      ></auto-select>
                    </v-col>
                    <v-col style="margin-top:20px;margin-left:3px" cols="auto">
                      <v-icon v-bind="attrs" v-on="on">
                        help
                      </v-icon>
                    </v-col>
                  </v-row>
                </v-container>
              </template>
              <span>
                {{
                  $t(
                    "components.plancreationwizard.additional_data_grouping_tooltip"
                  )
                }}
              </span>
            </v-tooltip>
          </v-col>
          <v-col
            cols="12"
            v-show="additionalDataGrouping === 'Coverage Groups'"
          >
            <pbas-data-table
              :showSelect="false"
              ref="coverageGroupsTable"
              :dataProvider="coverageGroupsProvider"
              :rowActions="coverageGroupsRowActions"
              :floatActions="coverageGroupsFloatActions"
              fileNamePrefix="coverageGroups"
              identifier="coverageGroups"
              @error="error = $event"
            ></pbas-data-table>
          </v-col>
          <v-col
            cols="12"
          >
            <reports-insurance-carriers :planInsuranceCarriers="planInsuranceCarriers" @plan-insurance-carriers="$emit('plan-insurance-carriers', $event)"/>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <v-dialog
      scrollable
      v-model="dialogs.coverageGroup"
      persistent
      max-width="1000"
    >
      <material-card
        :title="$t(coverageGroupsFormTitle)"
        :icon="coverageGroupsFormIcon"
        :color="formColor"
      >
        <v-card-text v-if="coverageGroupItem">
          <v-form ref="coverageGroupForm" v-model="validCoverageGroup">
            <v-container fluid fill-height>
              <v-row wrap>
                <v-col cols="12" md="3">
                  <no-autocomplete-textfield
                    :value="coverageGroupItem.Name"
                    @change="coverageGroupItem.Name = $event"
                    :label="$t('globals.name')"
                    :rules="[$rules.required]"
                    class="required"
                  ></no-autocomplete-textfield>
                </v-col>
                <v-col cols="12" md="3">
                  <trans-multi-select2
                    v-model="coverageGroupItem.Coverage"
                    :items="coverageForGroups"
                    :label="$t('globals.coverage')"
                    :rules="[$rules.atLeastOne]"
                    field="Name"
                    itemValue="Identifier"
                    :displayFunc="
                      (item, str) =>
                        `${str} (${truncate(trans(item, 'Description'), 40)})`
                    "
                  ></trans-multi-select2>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelCoverageGroup()">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            :disabled="!validCoverageGroup"
            color="primary"
            @click="saveCoverageGroupItem"
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog
      v-model="dialogs.deleteCoverageGroup"
      scrollable
      persistent
      max-width="400"
    >
      <material-card
        :title="$t('components.plancreationwizard.remove_coverage_group')"
        :color="$vuetify.theme.themes.light.error"
        icon="remove"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color text @click="dialogs.deleteCoverageGroup = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="primary" text @click="removeCoverageGroupItem">
            {{ $t("globals.remove") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
  </material-card>
</template>
<script>
import ReportsInsuranceCarriers from "@/components/PlanBuilder/ReportsInsuranceCarriers"
import AutoSelect from "@/components/AutoSelect";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import TransMultiSelect2 from "@/components/TransMultiSelect2";
import {
  InMemoryProvider,
  InMemoryHeader,
  InMemoryString,
} from "pbas-vue-components";
import { translate2 } from "@/trans2";

export default {
  props: [
    "defaultDataGrouping",
    "additionalDataGrouping",
    "hasClasses",
    "coverageGroups",
    "coverage",
    "disabled",
    "planInsuranceCarriers"
    // "groupByClass"
  ],
  components: {
    AutoSelect,
    NoAutocompleteTextfield,
    TransMultiSelect2,
    ReportsInsuranceCarriers
  },
  computed: {
    coverageGroupsFormTitle() {
      return this.coverageGroupIsNew
        ? "components.plancreationwizard.create_new_coverage_group"
        : "components.plancreationwizard.edit_coverage_group";
    },
    coverageGroupsFormIcon() {
      return this.coverageGroupIsNew ? "add" : "edit";
    },
    formColor() {
      return this.coverageGroupIsNew ? this.$vuetify.theme.themes.light.success : this.$vuetify.theme.themes.light.primary;
    },
  },
  data() {
    return {
      validForm: false,
      validCoverageGroup: false,
      dialogs: {
        coverageGroup: false,
        deleteCoverageGroup: false,
      },
      additionalDataGroupingItems: ["Coverage Groups"],
      defaultDataGroupingItems: [],
      coverageForGroups: [],
      coverageGroupItem: null,
      coverageGroupIsNew: false,
      coverageGroupsFloatActions: [
        {
          name: "addCoverageGroups",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => {
            this.coverageGroupItem = {
              Identifier: Math.random().toString(),
            };
            this.coverageGroupIsNew = true;
            this.coverageForGroups = JSON.parse(JSON.stringify(this.coverage));
            for (const coverageGroup of this.coverageGroups) {
              for (const coverage of coverageGroup.Coverage) {
                this.coverageForGroups = this.coverageForGroups.filter(
                  (o) => o.Identifier !== coverage.Identifier
                );
              }
            }
            this.dialogs.coverageGroup = true;
          },
          visible: () => !this.disabled,
        },
      ],
      coverageGroupsRowActions: [
        {
          name: "editCoverageGroups",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.coverageGroupItem = JSON.parse(JSON.stringify(item));
            this.coverageGroupIsNew = false;
            this.coverageForGroups = JSON.parse(JSON.stringify(this.coverage));
            for (const coverageGroup of this.coverageGroups) {
              for (const coverage of coverageGroup.Coverage) {
                this.coverageForGroups = this.coverageForGroups.filter(
                  (o) => o.Identifier !== coverage.Identifier
                );
              }
            }
            for (const cov of item.Coverage) {
              this.coverageForGroups.push(JSON.parse(JSON.stringify(cov)));
            }
            this.dialogs.coverageGroup = true;
          },
          visible: () => !this.disabled,
        },
        {
          name: "removeCoverageGroups",
          icon: "delete",
          title: "globals.remove",
          handler: (item) => {
            this.coverageGroupItem = JSON.parse(JSON.stringify(item));
            this.dialogs.deleteCoverageGroup = true;
          },
          visible: () => !this.disabled,
        },
      ],
      coverageGroupsProvider: undefined,
      coverageGroupsHeaders: [
        new InMemoryHeader({
          title: "headers.coveragegroups.coveragegroup",
          type: new InMemoryString("Name"),
          sortable: true,
          visible: true,
          allowVisibilityChange: true,
          searchable: true,
        }),
        new InMemoryHeader({
          title: "headers.coveragegroups.coverage",
          type: new InMemoryString("Coverage[]/Translations[]/Name", {
            getString: (item) => {
              return item.Coverage.map(
                (o) =>
                  translate2(o, "Name") +
                  " (" +
                  translate2(o, "Description") +
                  ")"
              ).join(", ");
            },
            getHtml: (item, type) => {
              return (
                "<div style='word-wrap: break-word; white-space: pre-wrap; max-width: 200px'>" +
                type.getString(item, type) +
                "</div>"
              );
            },
          }),
          sortable: false,
          visible: true,
          allowVisibilityChange: true,
          searchable: true,
        }),
      ],
    };
  },
  watch: {
    validForm: {
      handler(v) {
        this.$emit("valid", v);
      },
    },
    hasClasses: {
      handler(v) {
        if (v) {
          this.defaultDataGroupingItems = ["Member Groups", "Classes"];
        } else {
          this.defaultDataGroupingItems = ["Member Groups"];
        }
      },
    },
    coverageGroups: {
      handler(v) {
        if (this.coverageGroupsProvider===null || this.coverageGroupsProvider === undefined) {
          this.coverageGroupsProvider = new InMemoryProvider(
            v,
            this.coverageGroupsHeaders
          );
        } else {
          this.$nextTick(()=>{
            this.coverageGroupsProvider.items = v;
          this.$refs.coverageGroupsTable.getDataAsync();
          })
        }
        // this.validFunc();
      },
    },
    additionalDataGrouping: {
      handler(v) {
        // if (v !== "Coverage Groups") {
        //   this.coverageGroups = [];
        // }
        this.coverageGroupsProvider = new InMemoryProvider(
          this.coverageGroups,
          this.coverageGroupsHeaders
        );
        // this.validFunc();
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.hasClasses) {
      this.defaultDataGroupingItems = ["Member Groups", "Classes"];
    } else {
      this.defaultDataGroupingItems = ["Member Groups"];
    }
    this.$nextTick(() => {
      this.validForm = this.$refs.formReportGroup.validate();
    });
  },
  methods: {
    truncate(str, n, useWordBoundary) {
      if (str.length <= n) {
        return str;
      }
      const subString = str.substr(0, n - 1); // the original check
      return (
        (useWordBoundary
          ? subString.substr(0, subString.lastIndexOf(" "))
          : subString) + "..."
      );
    },
    trans(item, str) {
      return translate2(item, str);
    },
    saveCoverageGroupItem() {
      let item = JSON.parse(JSON.stringify(this.coverageGroupItem));
      if (this.coverageGroupIsNew) {
        this.addCoverageGroupItem(item);
      } else {
        this.updateCoverageGroupItem(item);
      }
    },
    updateCoverageGroupItem(item) {
      let coverageGroupItem;
      coverageGroupItem = this.coverageGroupsProvider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.coverageGroupsProvider.items[
        this.coverageGroupsProvider.items.indexOf(coverageGroupItem)
      ] = item;

      this.closeCoverageGroup();
      this.$refs.coverageGroupsTable.getDataAsync();
      this.$emit("update-coverage-groups", this.coverageGroupsProvider.items);
    },
    addCoverageGroupItem(item) {
      this.coverageGroupsProvider.items.push(item);
      this.closeCoverageGroup();
      this.$refs.coverageGroupsTable.getDataAsync();
      this.$emit("update-coverage-groups", this.coverageGroupsProvider.items);
    },
    removeCoverageGroupItem() {
      let item = JSON.parse(JSON.stringify(this.coverageGroupItem));
      let coverageGroupItem = this.coverageGroupsProvider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.coverageGroupsProvider.items.splice(
        this.coverageGroupsProvider.items.indexOf(coverageGroupItem),
        1
      );

      // for (const cov of item.Coverage) {
      //   this.coverageForGroups.push(JSON.parse(JSON.stringify(cov)));
      // }

      this.closeCoverageGroup();
      this.$refs.coverageGroupsTable.getDataAsync();
      this.$emit("update-coverage-groups", this.coverageGroupsProvider.items);
    },
    cancelCoverageGroup() {
      this.closeCoverageGroup();
    },
    closeCoverageGroup() {
      this.dialogs.coverageGroup = false;
      this.dialogs.deleteCoverageGroup = false;
      this.coverageGroupItem = null;
      if (this.$refs.coverageGroupForm)
        this.$refs.coverageGroupForm.resetValidation();
      // this.validFunc();
    },
  },
};
</script>
